import type { CityConfig } from "../city-template/types";

export const antiochConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Antioch",
  state: "California",
  stateAbbr: "CA",
  slug: "antioch",
  route: "/antioch",
  title: "Emergency Passport Antioch, CA — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Antioch, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Contra Costa County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Antioch, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Antioch and East Bay travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Antioch%20California%20cityscape%20at%20golden%20hour%20with%20the%20San%20Joaquin-Sacramento%20River%20Delta%20in%20the%20foreground%2C%20modern%20suburban%20homes%20along%20the%20waterfront%2C%20rolling%20hills%20in%20the%20background%2C%20warm%20sunset%20light%20reflecting%20on%20calm%20water%2C%20professional%20landscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20serene%20suburban%20waterfront%20scene%20with%20natural%20beauty&width=1800&height=700&seq=antioch-hero-001&orientation=landscape",
  heroImageAlt: "Antioch California waterfront cityscape at golden hour with the Sacramento-San Joaquin River Delta",
  geoPosition: "38.0049;-121.8058",
  geoPlacename: "Antioch, California",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "95 Hawthorne St Suite 200, San Francisco, CA 94105",
    distance: "Located in downtown SF — approximately 45–60 minutes from Antioch via I-680 and I-80",
  },
  nearbyCitiesZips: [
    { name: "Antioch", zips: "94509, 94531" },
    { name: "Pittsburg", zips: "94565" },
    { name: "Brentwood", zips: "94513" },
    { name: "Oakley", zips: "94561" },
    { name: "Concord", zips: "94518–94521" },
    { name: "Walnut Creek", zips: "94595, 94596, 94597" },
    { name: "Pleasant Hill", zips: "94523" },
    { name: "Martinez", zips: "94553" },
    { name: "Clayton", zips: "94517" },
    { name: "Lafayette", zips: "94549" },
    { name: "Discovery Bay", zips: "94505" },
    { name: "Bethel Island", zips: "94511" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent East Bay departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Los Angeles", route: "/los-angeles" },
    { name: "Emergency passport help in San Francisco", route: "/san-francisco" },
    { name: "Fast passport renewal in San Jose", route: "/san-jose" },
    { name: "Urgent passport services in Sacramento", route: "/sacramento" },
    { name: "Same-day passport help near Concord", route: "/concord" },
    { name: "Passport courier services near Vallejo", route: "/vallejo" },
    { name: "Rush passport help near Stockton", route: "/stockton" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Antioch remotely via secure courier. We do not have a walk-in office in Antioch — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for East Bay travelers.",
  fpcOfficeAddress: "400 S El Camino Real, San Mateo, CA 94402",
  fpcOfficeDistance: "Serving all of Contra Costa County",
  serviceAreaIntro: "From the waterfront neighborhoods along the Delta to the hillside communities in the east, Fast Passport Center serves the entire Antioch area. We regularly help travelers from Pittsburg, Brentwood, Oakley, and Discovery Bay get their passports fast.",
  nearbyCitiesIntro: "We serve all Antioch neighborhoods and nearby East Bay cities including Pittsburg, Brentwood, Oakley, Concord, and Martinez. If you are in Contra Costa County or nearby Solano County, we have you covered.",
  howItWorksIntro: "Most Antioch travelers complete everything online and ship documents overnight. No need to drive to San Francisco or wait in line at the regional agency.",
  mistakesIntro: "With SFO and OAK serving as the closest international airports, Antioch travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "East Bay and Delta area travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Antioch? Here are the most common ones we hear from Contra Costa County travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  dmvWebsite: "https://www.dmv.ca.gov",
  dmvLabel: "California DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent East Bay international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Jennifer M.",
      location: "Antioch, CA",
      rating: 5,
      text: "My family trip to Mexico was in 4 days and my daughter's passport was expired. Fast Passport Center got us a new one in 48 hours. Saved our entire vacation.",
      service: "Child Passport",
    },
    {
      name: "Robert K.",
      location: "Brentwood, CA",
      rating: 5,
      text: "Needed to renew for a business trip to London. The process was smooth, they reviewed my documents, and I had my passport in 3 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Lisa T.",
      location: "Pittsburg, CA",
      rating: 5,
      text: "Lost my passport the week before my cruise to the Caribbean. They walked me through the DS-64 process and got me a replacement in 2 days. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "David H.",
      location: "Oakley, CA",
      rating: 5,
      text: "As a frequent international traveler from the East Bay, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "antioch-1",
      name: "Antioch Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2730 Fulton Shipyard Rd",
      city: "Antioch",
      state: "CA",
      zip: "94509",
      phone: "(925) 757-7114",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.2 miles from downtown Antioch",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "antioch-2",
      name: "Pittsburg Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2150 Loveridge Rd",
      city: "Pittsburg",
      state: "CA",
      zip: "94565",
      phone: "(925) 432-2601",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "5.8 miles from Antioch",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "antioch-3",
      name: "Brentwood Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "200 Birch St",
      city: "Brentwood",
      state: "CA",
      zip: "94513",
      phone: "(925) 634-2191",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "8.2 miles from Antioch",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for eastern Contra Costa residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "antioch-4",
      name: "Concord Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2170 Concord Blvd",
      city: "Concord",
      state: "CA",
      zip: "94520",
      phone: "(925) 827-4550",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "12 miles from Antioch",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for central Contra Costa. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "antioch-5",
      name: "Walnut Creek Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2070 Bonanza St",
      city: "Walnut Creek",
      state: "CA",
      zip: "94596",
      phone: "(925) 935-6291",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "18 miles from Antioch",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for western Contra Costa residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "antioch-6",
      name: "Contra Costa County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "555 Escobar St",
      city: "Martinez",
      state: "CA",
      zip: "94553",
      phone: "(925) 335-7900",
      hours: "Mon–Fri: 8:00 AM – 4:00 PM",
      distance: "14 miles from Antioch",
      website: "https://www.contracosta.ca.gov/",
      websiteLabel: "Contra Costa County",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown San Francisco", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "East Bay photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Antioch acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Antioch applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the SF regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many East Bay families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Antioch travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Antioch." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Antioch address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Antioch before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Antioch for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround for most Contra Costa County travelers." },
    { q: "How fast can I renew my passport in Antioch?", a: "Fast Passport Center offers fast passport renewal in Antioch in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in Antioch in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Antioch?", a: "Same-day passport appointments are only available at the SF Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Antioch residents." },
    { q: "Can I expedite a child passport in Antioch?", a: "Yes. Fast Passport Center expedites child passports in Antioch for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk Contra Costa County families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Antioch with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Antioch?",
      a: "With Fast Passport Center's emergency courier service, Antioch travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the San Francisco Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Antioch?",
      a: "Popular options include the Antioch Main Post Office (94509), Pittsburg Post Office (94565), Brentwood Post Office (94513), and Contra Costa County Clerk in Martinez (94553). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Antioch?",
      a: "Same-day passports are only available at the SF Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in Discovery Bay or Bethel Island?",
      a: "We serve all of Contra Costa County including Discovery Bay, Bethel Island, Oakley, and Brentwood. Our overnight shipping labels work from any UPS location, so your location within the East Bay doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Antioch?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Antioch?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Antioch, Pittsburg, and Brentwood offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Antioch?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Antioch?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk East Bay families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Antioch", address: "1810 A St, Antioch, CA 94509", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/" },
    { name: "Walgreens — Antioch", address: "2849 Lone Tree Way, Antioch, CA 94509", hours: "Mon–Sun: 7AM–11PM", website: "https://www.walgreens.com/" },
    { name: "FedEx Office — Antioch", address: "5779 Lone Tree Way, Antioch, CA 94531", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM", website: "https://www.fedex.com/" },
    { name: "UPS Store — Brentwood", address: "8620 Brentwood Blvd, Brentwood, CA 94513", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM", website: "https://www.theupsstore.com/" },
    { name: "Walmart — Pittsburg", address: "2200 Loveridge Rd, Pittsburg, CA 94565", hours: "Mon–Sun: 6AM–11PM", website: "https://www.walmart.com/" },
    { name: "Rite Aid — Concord", address: "2990 Treat Blvd, Concord, CA 94518", hours: "Mon–Sun: 8AM–10PM", website: "https://www.riteaid.com/" },
    { name: "Postal Annex — Oakley", address: "2100 Main St, Oakley, CA 94561", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM", website: "https://www.postalannex.com/" },
    { name: "Costco — Concord", address: "2400 Monument Blvd, Concord, CA 94520", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM", website: "https://www.costco.com/" },
  ],
  dmvLocations: [
    {
      name: "DMV — Antioch",
      address: "2800 E Leland Rd, Pittsburg, CA 94565",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Concord",
      address: "2260 Diamond Blvd, Concord, CA 94520",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Walnut Creek",
      address: "1991 N Main St, Walnut Creek, CA 94596",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Brentwood",
      address: "555 Main St, Brentwood, CA 94513",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for eastern Contra Costa residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "SFO",
      name: "San Francisco International Airport",
      distance: "45 miles from Antioch",
      address: "San Francisco, CA 94128",
      note: "Major international hub for long-haul flights to Asia, Europe, and South America. Accessible via I-680 and I-80.",
      flightUrl: "https://www.flysfo.com/",
      flightLabel: "SFO Flight Info",
      featured: true,
    },
    {
      code: "OAK",
      name: "Oakland International Airport",
      distance: "35 miles from Antioch",
      address: "1 Airport Dr, Oakland, CA 94621",
      note: "Convenient for East Bay residents. International flights to Mexico, Canada, and connections to Europe.",
      flightUrl: "https://www.oaklandairport.com/",
      flightLabel: "Oakland Airport",
      featured: false,
    },
    {
      code: "SMF",
      name: "Sacramento International Airport",
      distance: "55 miles from Antioch",
      address: "6900 Airport Blvd, Sacramento, CA 95837",
      note: "Alternative for northern California flights. Growing international connections.",
      flightUrl: "https://www.sacramento.aero/",
      flightLabel: "Sacramento Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    { label: "California CDPH — Birth, Death & Marriage Records", url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx" },
    { label: "Contra Costa County Clerk-Recorder — Birth, Death & Marriage Records", url: "https://www.ccclerkrec.us/" },
    { label: "Contra Costa Superior Court — Divorce Records", url: "https://www.cc-courts.org/" },
  ],
};